import { useState, type ReactNode } from "react";
import { Link, useHref } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import {
  Check,
  ExternalLink,
  Info,
  Lock,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { ConfirmDialog } from "@/base/ConfirmDialog";
import { buttonClass } from "@/components/buttonStyles";
import { statusBadgeClass } from "@/lib/listFormat";
import { formatBondDate } from "@/lib/bondValueDisplay";
import type {
  BidResultButtonState,
  BidResultTone,
  GiaButtonState,
  GiaButtonTone,
  HeaderPill,
  PillTone,
  ProgramButtonGlyph,
  ProgramButtonTone,
  ProgramEnrollmentState,
  SubmissionActionState,
  SubmissionRowAction,
} from "@/lib/submissionShell";

// BRK-S399, BRK-S502 — the pieces of the submission page's v2 shell that sit
// above the tabs: the header's pills, the button row of three (Program
// Enrollment, GIA Form, Bid Result), the fixed action row of six, and the
// "Rider request pending" banner. They RENDER what `src/lib/submissionShell.ts` read off the
// order view; none of them decides whether an action is available.

/** A pill's tone on the shared badge palette (`statusBadgeClass`). */
const TONE_KEY: Record<PillTone, string> = {
  done: "approved",
  active: "submitted",
  attention: "referred",
  danger: "declined",
  neutral: "draft",
};

/** The functional red the wireframe draws a hold, an overage and a missing
 *  program in (the shared palette's `declined` is the accent, not red). */
export const DANGER_BADGE = "bg-brk-danger/10 text-brk-danger";

export function Pill({ pill }: { pill: HeaderPill }) {
  return (
    <span
      data-tone={pill.tone}
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
        pill.tone === "danger"
          ? DANGER_BADGE
          : statusBadgeClass(TONE_KEY[pill.tone])
      }`}
    >
      {pill.label}
    </span>
  );
}

/**
 * An action the server has not granted: it stays in its place, greyed with a
 * lock (the wireframe), and says why — in a tooltip for the eye and in its
 * accessible name for a screen reader ("Request Rider (unavailable): Riders
 * can only…"). `aria-disabled` rather than `disabled`, so it stays focusable
 * and the reason can be reached from the keyboard; a click does nothing.
 */
export function LockedButton({
  label,
  reason,
  className,
}: {
  label: string;
  reason: string | null;
  className: string;
}) {
  const button = (
    <button
      type="button"
      aria-disabled="true"
      aria-label={
        reason ? `${label} (unavailable): ${reason}` : `${label} (unavailable)`
      }
      onClick={(e) => e.preventDefault()}
      className={`${className} cursor-not-allowed opacity-60`}
    >
      <Lock aria-hidden="true" data-lock className="h-3.5 w-3.5" />
      {label}
    </button>
  );
  // No reason served: nothing to say.
  return reason ? (
    <Tooltip title={reason} arrow enterTouchDelay={0}>
      {button}
    </Tooltip>
  ) : (
    button
  );
}

/** The button row's green (done) and functional red (needs doing), shared by
 *  the GIA Form and Program Enrollment buttons. */
const GREEN =
  "border-green-300 bg-green-100 text-green-700 hover:bg-green-200 dark:border-green-400/40 dark:bg-green-400/15 dark:text-green-300 dark:hover:bg-green-400/25";
const RED =
  "border-brk-danger bg-brk-danger/10 text-brk-danger hover:bg-brk-danger/15";
const AMBER =
  "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-400/50 dark:bg-amber-400/15 dark:text-amber-200 dark:hover:bg-amber-400/25";

/** The GIA Form button's colours: the shared badge palette's green and the
 *  functional red, so it reads like every other done / needs-doing thing. */
const GIA_TONE: Record<
  GiaButtonTone,
  { className: string; Glyph: LucideIcon | null }
> = {
  signed: { className: GREEN, Glyph: Check },
  needed: { className: RED, Glyph: Info },
  neutral: { className: "", Glyph: null },
};

/**
 * The GIA Form button (BRK-S423): green with a tick once signed, red with ⓘ
 * and the advisory tooltip while it is not, neutral otherwise — all off the
 * served `gia.status` (`giaButton`). It opens the GIA Form dialog whatever
 * its colour; the state is in its accessible name, the tooltip its
 * description.
 */
export function GiaFormButton({
  state,
  onOpen,
}: {
  state: GiaButtonState;
  onOpen: () => void;
}) {
  const { className, Glyph } = GIA_TONE[state.tone];
  const button = (
    <button
      type="button"
      onClick={onOpen}
      aria-label={state.name}
      aria-haspopup="dialog"
      data-tone={state.tone}
      className={`${buttonClass({ variant: "secondary", size: "sm" })} ${className}`}
    >
      {Glyph && <Glyph aria-hidden="true" className="h-4 w-4" />}
      {state.label}
    </button>
  );
  return state.tooltip ? (
    <Tooltip title={state.tooltip} arrow enterTouchDelay={0} describeChild>
      {button}
    </Tooltip>
  ) : (
    button
  );
}

/** The Program Enrollment button's colours, per state. */
const PROGRAM_TONE: Record<ProgramButtonTone, string> = {
  ok: GREEN,
  upgrade: AMBER,
  danger: RED,
  neutral: "",
};

/** ✓ within, ⓘ needs attention, ⚠ … ↗ for the upgrade that leaves the page. */
const PROGRAM_GLYPH: Record<ProgramButtonGlyph, LucideIcon> = {
  check: Check,
  info: Info,
  external: TriangleAlert,
};

/** The Advance-redirect confirm's context — the wireframe's sentence. */
const ADVANCE_REDIRECT_TEXT =
  "This bond needs more capacity than Signal Express allows. You’re being taken to the Signal Advance application on the account page, where the client connects their bank and accounting to qualify for higher limits.";

/**
 * The Program Enrollment button (BRK-S422), rendering `programEnrollmentButton`:
 * No Program opens the account page's program tab to enroll; Express over a
 * limit asks first ("Go to the Signal Advance application?") and then opens
 * the account's `#advance-app` in a NEW tab, so the submission stays where it
 * was; every other state is status only — a focusable chip whose tooltip
 * names the state. Nothing here decides a state.
 */
export function ProgramEnrollmentButton({
  state,
  accountId,
}: {
  state: ProgramEnrollmentState;
  accountId: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const account = `/surety/accounts/${encodeURIComponent(accountId)}`;
  const advanceHref = useHref(`${account}?tab=program#advance-app`);
  const Glyph = state.glyph ? PROGRAM_GLYPH[state.glyph] : null;
  const name = `Program Enrollment: ${state.label}`;
  const className = `${buttonClass({ variant: "secondary", size: "sm" })} ${PROGRAM_TONE[state.tone]}`;
  const content = (
    <>
      {Glyph && <Glyph aria-hidden="true" className="h-4 w-4" />}
      {state.label}
      {/* ↗: it leaves for the account page (enroll) or the Advance
          application. */}
      {(state.action === "advance" || state.action === "enroll") && (
        <ExternalLink aria-hidden="true" className="h-4 w-4" />
      )}
    </>
  );

  let control: ReactNode;
  if (state.action === "enroll") {
    control = (
      <Link
        to={`${account}?tab=program`}
        aria-label={name}
        data-tone={state.tone}
        className={className}
      >
        {content}
      </Link>
    );
  } else if (state.action === "advance") {
    control = (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        aria-label={name}
        aria-haspopup="dialog"
        data-tone={state.tone}
        className={className}
      >
        {content}
      </button>
    );
  } else {
    // Status only: focusable when it has a tooltip, so the state can be
    // reached from the keyboard; no click.
    control = (
      <span
        tabIndex={state.tooltip ? 0 : undefined}
        aria-label={name}
        data-tone={state.tone}
        className={`${className} cursor-default ${
          state.tone === "neutral" ? "hover:bg-transparent" : ""
        }`}
      >
        {content}
      </span>
    );
  }

  return (
    <>
      {state.tooltip ? (
        <Tooltip title={state.tooltip} arrow enterTouchDelay={0} describeChild>
          {/* MUI needs a ref-able child that forwards its events. */}
          <span className="inline-flex min-w-0">{control}</span>
        </Tooltip>
      ) : (
        control
      )}
      {state.action === "advance" && (
        <ConfirmDialog
          open={confirming}
          question="Go to the Signal Advance application?"
          cancelLabel="Stay here"
          confirmLabel="Continue to Advance application"
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            window.open(advanceHref, "_blank", "noopener,noreferrer");
          }}
        >
          <div className="flex flex-col gap-2 text-sm text-brk-text">
            <p>{ADVANCE_REDIRECT_TEXT}</p>
            {state.capacity && <p className="font-medium">{state.capacity}</p>}
          </div>
        </ConfirmDialog>
      )}
    </>
  );
}

/** The Bid Result button's colours: the accent call to action, green for an
 *  award, grey for a lost bid. */
const BID_TONE: Record<BidResultTone, string> = {
  accent: "",
  won: GREEN,
  lost: "border-brk-border bg-brk-bg text-brk-dim",
  neutral: "",
};

/**
 * The button row of three (the October wireframe): Program Enrollment, named
 * for the program, against the capacity check (BRK-S422); GIA Form, red or
 * green, opening the GIA Form dialog (BRK-S423); Bid Result, the accent
 * button while PMS grants a report, the result once one is recorded, greyed
 * with PMS's reason otherwise (`bidResultButton`). Message Underwriter lives
 * on the action row.
 */
export function SubmissionButtonRow({
  program,
  accountId,
  gia,
  onOpenGia,
  bidResult,
  onOpenBidResult,
}: {
  program: ProgramEnrollmentState;
  accountId: string;
  gia: GiaButtonState;
  onOpenGia: () => void;
  bidResult: BidResultButtonState;
  onOpenBidResult: () => void;
}) {
  const bidClass = `${buttonClass({
    variant: bidResult.tone === "accent" ? "primary" : "secondary",
    size: "sm",
  })} ${BID_TONE[bidResult.tone]}`;
  return (
    <div
      role="group"
      aria-label="Submission status"
      className="flex min-w-0 flex-wrap items-center gap-2"
    >
      <ProgramEnrollmentButton state={program} accountId={accountId} />
      <GiaFormButton state={gia} onOpen={onOpenGia} />
      {bidResult.enabled ? (
        <button
          type="button"
          onClick={onOpenBidResult}
          aria-haspopup="dialog"
          data-tone={bidResult.tone}
          className={bidClass}
        >
          {bidResult.tone === "won" && (
            <Check aria-hidden="true" className="h-4 w-4" />
          )}
          {bidResult.label}
        </button>
      ) : (
        <LockedButton
          label={bidResult.label}
          reason={bidResult.reason}
          className={bidClass}
        />
      )}
    </div>
  );
}

/**
 * The fixed action row: the wireframe's six at every step, each enabled or
 * greyed (with a lock) exactly as the order view's `actions{}` says. It wraps
 * at 375px rather than scrolling the page sideways; Withdraw Bond is
 * red-outlined while it is available.
 */
export function SubmissionActionRow({
  actions,
  onSelect,
}: {
  actions: readonly SubmissionActionState[];
  onSelect: (key: SubmissionRowAction) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Submission actions"
      className="flex min-w-0 flex-wrap items-center gap-2"
    >
      {actions.map((a) => {
        const base = buttonClass({ variant: "secondary", size: "sm" });
        return a.enabled ? (
          <button
            key={a.key}
            type="button"
            onClick={() => onSelect(a.key)}
            className={`${base} ${
              a.key === "withdraw" ? "border-brk-danger text-brk-danger" : ""
            }`}
          >
            {a.label}
          </button>
        ) : (
          <LockedButton
            key={a.key}
            label={a.label}
            reason={a.reason}
            className={base}
          />
        );
      })}
    </div>
  );
}

/**
 * A rider waiting on underwriting (`changeRequest.kind === "rider"`): when it
 * was submitted (`bonds[].pendingRider.requestedAt`, m/d/yyyy — D-10) and
 * **Withdraw request** (BRK-S420). Presentational: `RiderPendingNotice` asks
 * first, sends the withdraw and passes its state in. Without `onWithdraw` —
 * no rider id to address — the button is shown and held.
 */
export function RiderPendingBanner({
  requestedAt,
  onWithdraw,
  busy = false,
  error = null,
  children,
}: {
  requestedAt: string | null;
  onWithdraw?: () => void;
  busy?: boolean;
  /** The withdraw's refusal, said inline; the banner stays. */
  error?: string | null;
  children?: ReactNode;
}) {
  return (
    <div
      role="status"
      className="flex min-w-0 flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-brk-text sm:flex-row sm:items-center sm:justify-between dark:border-amber-400/40 dark:bg-amber-400/10"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="font-medium">Rider request pending</p>
        {requestedAt !== null && (
          <p className="text-brk-dim">
            Submitted {formatBondDate(requestedAt)}
          </p>
        )}
        <p className="text-brk-dim">
          {children ??
            "Signal is reviewing the change. The bond stays as issued until it’s approved."}
        </p>
        {error && (
          <p role="alert" className="text-brk-danger">
            {error}
          </p>
        )}
      </div>
      <button
        type="button"
        disabled={onWithdraw === undefined || busy}
        onClick={onWithdraw}
        className={`shrink-0 ${buttonClass({ variant: "secondary", size: "sm" })}`}
      >
        {busy ? "Withdrawing…" : "Withdraw request"}
      </button>
    </div>
  );
}
